/**
 * Product Details Content
 * Detalhe/perfil de um registro de produto da base.
 * Redesign Design System: toolbar 3-zonas com breadcrumb (nunca seta-voltar),
 * galeria + seções à esquerda e painel de metadados/preço à direita (empilha no mobile).
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type {
  DatabaseDocumentResponse,
  DatabaseProductData,
  BusinessOpportunity,
  BusinessOpportunityType,
  OpportunityStatus,
  ProductItemStatus,
  ProductVariation
} from '@troia-v3/types';
import { Breadcrumb } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useDatabase } from '../../hooks/useDatabases';

interface ProductDetailsContentProps {
  document: DatabaseDocumentResponse<DatabaseProductData>;
  databaseId: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const formatFullPrice = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `R$ ${Math.round(value / 1000)}mil`;
  }
  return formatFullPrice(value);
};

const categoryLabels: Record<string, string> = {
  electronics: 'Eletrônicos',
  furniture: 'Móveis',
  clothing: 'Vestuário',
  food: 'Alimentos',
  books: 'Livros',
  toys: 'Brinquedos',
  sports: 'Esportes',
  beauty: 'Beleza',
  automotive: 'Automotivo',
  home_garden: 'Casa e Jardim',
  other: 'Outros'
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  outofstock: 'Sem estoque',
  discontinued: 'Descontinuado',
  archived: 'Arquivado'
};

const getItemStatusColor = (status: ProductItemStatus): { bg: string; text: string } => {
  const colors: Record<string, { bg: string; text: string }> = {
    active: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    inactive: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
    outofstock: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
    discontinued: { bg: 'var(--theme-warning-background)', text: 'var(--theme-warning-color)' },
    archived: { bg: 'var(--theme-muted-text)', text: '#ffffff' }
  };
  return colors[status] || { bg: 'var(--theme-card-background)', text: 'var(--theme-muted-text)' };
};

const getOpportunityTypeLabel = (type: BusinessOpportunityType): string => {
  const labels: Record<string, string> = {
    sale: 'Venda',
    rent: 'Aluguel',
    seasonal_rent: 'Temporada',
    daily_rent: 'Diária',
    lease: 'Arrendamento',
    exchange: 'Permuta',
    donation: 'Doação',
    auction: 'Leilão'
  };
  return labels[type] || type;
};

const getOpportunityStatusColor = (status: OpportunityStatus): { bg: string; text: string } => {
  const colors: Record<string, { bg: string; text: string }> = {
    active: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    inactive: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
    reserved: { bg: 'var(--theme-warning-background)', text: 'var(--theme-warning-color)' },
    in_negotiation: { bg: 'var(--theme-info-background)', text: 'var(--theme-info-color)' },
    completed: { bg: 'var(--theme-primary-background)', text: 'var(--theme-primary)' },
    cancelled: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' }
  };
  return colors[status] || { bg: 'var(--theme-card-background)', text: 'var(--theme-muted-text)' };
};

const stockUnitLabels: Record<string, string> = {
  unit: 'un.',
  kg: 'kg',
  liter: 'L',
  meter: 'm',
  box: 'cx',
  pack: 'pct'
};

const getTotalStock = (product: DatabaseProductData): number | null => {
  if (product.variations && product.variations.length > 0) {
    return product.variations.reduce((sum: number, v: ProductVariation) => sum + (v.stock || 0), 0);
  }
  return product.inventory?.stock ?? null;
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-card-background)',
  border: '1px solid var(--theme-card-border)',
};

/** Seção de conteúdo com cabeçalho (ícone + título) — padrão do Design System. */
const SectionCard: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  title: string;
  children: React.ReactNode;
  bodyClassName?: string;
  testId?: string;
}> = ({ icon, title, children, bodyClassName, testId }) => (
  <section className="rounded-xl overflow-hidden" style={cardStyle} data-testid={testId}>
    <div
      className="flex items-center gap-2.5 px-4 py-3 border-b"
      style={{ borderColor: 'var(--theme-card-border)' }}
    >
      <Icon name={icon} className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
      <span className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
        {title}
      </span>
    </div>
    <div className={bodyClassName ?? 'p-4'}>{children}</div>
  </section>
);

export const ProductDetailsContent: React.FC<ProductDetailsContentProps> = ({
  document,
  databaseId,
  onEdit,
  onDelete
}) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);

  const product = document.data;
  const photos = product.media?.photos || [];
  const hasPhotos = photos.length > 0;
  const totalStock = getTotalStock(product);
  const unit = stockUnitLabels[product.inventory?.stockUnit || 'unit'] || 'un.';
  const statusColor = getItemStatusColor(product.itemStatus);

  // Nome da base para o breadcrumb (rota de módulo — usuário já tem acesso).
  const { data: database } = useDatabase(databaseId);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleBackToDatabases = () => navigate('/databases');
  const handleBackToDatabase = () => navigate(`/databases/${databaseId}`);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Estatísticas de estoque (algumas condicionais) — superset dos tiles originais.
  const stockStats: { label: string; value: React.ReactNode; color?: string }[] = [
    {
      label: `Total (${unit})`,
      value: totalStock !== null ? totalStock : 'N/A',
    },
  ];
  if (product.inventory?.lowStockThreshold) {
    stockStats.push({
      label: 'Alerta estoque baixo',
      value: product.inventory.lowStockThreshold,
      color: 'var(--theme-warning-color)',
    });
  }
  if (product.variations && product.variations.length > 0) {
    stockStats.push({
      label: product.variations.length === 1 ? 'Variação' : 'Variações',
      value: product.variations.length,
    });
  }
  stockStats.push({
    label: 'Controle de estoque',
    value: product.inventory?.trackInventory ? 'Rastreado' : 'Não rastreado',
    color: product.inventory?.trackInventory
      ? 'var(--theme-success-color)'
      : 'var(--theme-muted-text)',
  });

  // Linhas do painel "Informações" (algumas condicionais).
  const infoRows: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Categoria',
      value: (
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
        >
          {categoryLabels[product.category] || product.category}
        </span>
      ),
    },
  ];
  if (product.sku) {
    infoRows.push({
      label: 'SKU',
      value: (
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="font-mono truncate" title={product.sku}>
            {product.sku}
          </span>
          <button
            onClick={() => copyToClipboard(product.sku!)}
            className="inline-flex items-center justify-center w-6 h-6 rounded-md flex-none transition-colors"
            title="Copiar SKU"
            data-testid="product-copy-sku"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon name="copy" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          </button>
        </span>
      ),
    });
  }
  if (product.brand) {
    infoRows.push({ label: 'Marca', value: product.brand });
  }
  infoRows.push({
    label: 'Status',
    value: (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
        style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
        {statusLabels[product.itemStatus] || product.itemStatus}
      </span>
    ),
  });

  // Linhas do painel "Metadados".
  const metaRows: { label: string; value: React.ReactNode }[] = [
    { label: 'Criado em', value: new Date(document.createdAt).toLocaleDateString('pt-BR') },
    { label: 'Atualizado em', value: new Date(document.updatedAt).toLocaleDateString('pt-BR') },
  ];
  if (document.metadata?.source) {
    metaRows.push({
      label: 'Origem',
      value:
        document.metadata.source === 'manual'
          ? 'Cadastro manual'
          : document.metadata.source === 'integration'
          ? 'Integração'
          : document.metadata.source === 'import'
          ? 'Importação'
          : 'API',
    });
  }

  const renderRows = (rows: { label: string; value: React.ReactNode }[]) =>
    rows.map((row, i) => (
      <div
        key={row.label}
        className={`flex items-center gap-3 px-4 py-2.5 text-sm ${i > 0 ? 'border-t' : ''}`}
        style={i > 0 ? { borderColor: 'var(--theme-card-border)' } : undefined}
      >
        <span className="w-24 flex-none" style={{ color: 'var(--theme-muted-text)' }}>
          {row.label}
        </span>
        <div
          className="flex-1 min-w-0 font-medium break-words text-right sm:text-left"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {row.value}
        </div>
      </div>
    ));

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="product-details-content"
    >
      {/* Toolbar 3 zonas (DS 3.3): breadcrumb à esquerda, ações à direita */}
      <header
        className="flex-none h-14 flex items-center gap-3 px-4 sm:px-6 border-b"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderColor: 'var(--theme-header-border)',
        }}
      >
        {/* Breadcrumb shared (F25-31; F26: hover morto corrigido pelo shared) */}
        <Breadcrumb
          items={[
            { label: 'Bases de dados', onNavigate: handleBackToDatabases, testid: 'product-breadcrumb-databases' },
            { label: database?.name || 'Base', onNavigate: handleBackToDatabase, testid: 'product-breadcrumb-database' },
            { label: product.name, testid: 'product-breadcrumb-current' },
          ]}
          mobileLegend={`Bases de dados · ${database?.name || 'Base'}`}
          data-testid="product-breadcrumb"
        />

        <div className="flex items-center gap-2 flex-none">
          {onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--theme-button-primary)',
                color: 'var(--theme-button-primary-text, #ffffff)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
              }}
              data-testid="product-edit-button"
            >
              <Icon name="edit" className="w-4 h-4" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold transition-colors"
              style={{ color: 'var(--theme-error-color)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-error-background)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Excluir"
              data-testid="product-delete-button"
            >
              <Icon name="trash" className="w-4 h-4" />
              <span className="hidden sm:inline">Excluir</span>
            </button>
          )}
        </div>
      </header>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 md:gap-6 items-start">
            {/* Coluna principal — galeria + seções */}
            <div className="flex flex-col gap-4 md:gap-6 min-w-0">
              {/* Galeria de imagens */}
              <section className="rounded-xl overflow-hidden" style={cardStyle} data-testid="product-gallery">
                <div className="p-4">
                  {hasPhotos ? (
                    <div className="relative">
                      <img
                        src={photos[currentImageIndex]}
                        alt={`${product.name} - Foto ${currentImageIndex + 1}`}
                        className="w-full h-64 md:h-[340px] object-cover rounded-lg cursor-zoom-in"
                        onClick={() => setIsImageFullscreen(true)}
                        data-testid="product-gallery-image"
                      />
                      {photos.length > 1 && (
                        <>
                          <button
                            onClick={handlePrevImage}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
                            data-testid="product-gallery-prev"
                          >
                            <Icon name="chevron-left" className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleNextImage}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
                            data-testid="product-gallery-next"
                          >
                            <Icon name="chevron-right" className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-semibold tabular-nums">
                        {currentImageIndex + 1} / {photos.length}
                      </span>
                      <button
                        onClick={() => setIsImageFullscreen(true)}
                        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
                        title="Ampliar"
                        data-testid="product-gallery-maximize"
                      >
                        <Icon name="maximize" className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="h-64 md:h-[340px] flex items-center justify-center rounded-lg"
                      style={{ backgroundColor: 'var(--theme-page-background)' }}
                    >
                      <Icon name="package" className="w-16 h-16" style={{ color: 'var(--theme-muted-text)' }} />
                    </div>
                  )}

                  {photos.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto">
                      {photos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all"
                          style={{
                            border:
                              index === currentImageIndex
                                ? '2px solid var(--theme-primary)'
                                : '2px solid var(--theme-card-border)',
                          }}
                          data-testid={`product-gallery-thumb-${index}`}
                        >
                          <img src={photo} alt={`Miniatura ${index + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Estoque */}
              <SectionCard icon="box" title="Estoque" testId="product-inventory">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {stockStats.map((stat) => (
                    <div key={stat.label}>
                      <div
                        className="text-[11px] font-bold uppercase tracking-wide"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {stat.label}
                      </div>
                      <div
                        className="text-xl font-extrabold tabular-nums mt-0.5"
                        style={{ color: stat.color ?? 'var(--theme-card-text)' }}
                      >
                        {stat.value}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Variações */}
              {product.variations && product.variations.length > 0 && (
                <SectionCard icon="layers" title="Variações" bodyClassName="p-0" testId="product-variations">
                  <div>
                    {product.variations.map((variation, index) => (
                      <div
                        key={variation.id}
                        className={`px-4 py-3.5 ${index > 0 ? 'border-t' : ''}`}
                        style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : undefined}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <span className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
                            {variation.name}
                          </span>
                          <div className="flex items-center gap-2 flex-none">
                            <span
                              className="text-sm font-semibold tabular-nums"
                              style={{
                                color: variation.stock === 0 ? 'var(--theme-error-color)' : 'var(--theme-card-text)',
                              }}
                            >
                              {variation.stock} {unit}
                            </span>
                            {variation.itemStatus && (
                              <span
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: getItemStatusColor(variation.itemStatus).bg,
                                  color: getItemStatusColor(variation.itemStatus).text,
                                }}
                              >
                                {statusLabels[variation.itemStatus] || variation.itemStatus}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Atributos + identificadores */}
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(variation.attributes).map(([key, value]) => (
                            <span
                              key={key}
                              className="px-2 py-1 rounded text-xs"
                              style={{
                                backgroundColor: 'var(--theme-primary-background)',
                                color: 'var(--theme-primary)',
                              }}
                            >
                              {key}: {value}
                            </span>
                          ))}
                          {variation.sku && (
                            <span
                              className="px-2 py-1 rounded text-xs font-mono"
                              style={{
                                backgroundColor: 'var(--theme-page-background)',
                                color: 'var(--theme-muted-text)',
                                border: '1px solid var(--theme-card-border)',
                              }}
                            >
                              SKU: {variation.sku}
                            </span>
                          )}
                          {variation.barcode && (
                            <span
                              className="px-2 py-1 rounded text-xs font-mono"
                              style={{
                                backgroundColor: 'var(--theme-page-background)',
                                color: 'var(--theme-muted-text)',
                                border: '1px solid var(--theme-card-border)',
                              }}
                            >
                              Barcode: {variation.barcode}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Descrição */}
              <SectionCard icon="file-text" title="Descrição" testId="product-description">
                <p
                  className="text-sm whitespace-pre-wrap leading-relaxed"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {product.description || 'Sem descrição disponível.'}
                </p>
              </SectionCard>

              {/* Dimensões */}
              {product.dimensions && (
                <SectionCard icon="ruler" title="Dimensões" testId="product-dimensions">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    {product.dimensions.weight !== undefined && (
                      <div>
                        <div style={{ color: 'var(--theme-muted-text)' }}>Peso</div>
                        <div className="font-medium mt-0.5" style={{ color: 'var(--theme-card-text)' }}>
                          {product.dimensions.weight}{' '}
                          {product.dimensions.unit === 'kg' || product.dimensions.unit === 'lb'
                            ? product.dimensions.unit
                            : 'kg'}
                        </div>
                      </div>
                    )}
                    {product.dimensions.width !== undefined && (
                      <div>
                        <div style={{ color: 'var(--theme-muted-text)' }}>Largura</div>
                        <div className="font-medium mt-0.5" style={{ color: 'var(--theme-card-text)' }}>
                          {product.dimensions.width}{' '}
                          {product.dimensions.unit === 'cm' || product.dimensions.unit === 'inch'
                            ? product.dimensions.unit
                            : 'cm'}
                        </div>
                      </div>
                    )}
                    {product.dimensions.height !== undefined && (
                      <div>
                        <div style={{ color: 'var(--theme-muted-text)' }}>Altura</div>
                        <div className="font-medium mt-0.5" style={{ color: 'var(--theme-card-text)' }}>
                          {product.dimensions.height}{' '}
                          {product.dimensions.unit === 'cm' || product.dimensions.unit === 'inch'
                            ? product.dimensions.unit
                            : 'cm'}
                        </div>
                      </div>
                    )}
                    {product.dimensions.depth !== undefined && (
                      <div>
                        <div style={{ color: 'var(--theme-muted-text)' }}>Profundidade</div>
                        <div className="font-medium mt-0.5" style={{ color: 'var(--theme-card-text)' }}>
                          {product.dimensions.depth}{' '}
                          {product.dimensions.unit === 'cm' || product.dimensions.unit === 'inch'
                            ? product.dimensions.unit
                            : 'cm'}
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>
              )}
            </div>

            {/* Coluna lateral — preço/oportunidades + metadados */}
            <aside className="flex flex-col gap-4 md:gap-6 min-w-0">
              {/* Oportunidades de Negócio */}
              <SectionCard icon="dollar-sign" title="Oportunidades de Negócio" testId="product-opportunities">
                {product.opportunities && product.opportunities.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {product.opportunities.map((opportunity) => (
                      <OpportunityCard key={opportunity.id} opportunity={opportunity} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    Nenhuma oportunidade cadastrada.
                  </p>
                )}
              </SectionCard>

              {/* Informações */}
              <SectionCard icon="info" title="Informações" bodyClassName="p-0" testId="product-info">
                <div>{renderRows(infoRows)}</div>
              </SectionCard>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <SectionCard icon="tag" title="Tags" testId="product-tags">
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: 'var(--theme-primary-background)',
                          color: 'var(--theme-primary)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Metadados */}
              <SectionCard icon="clock" title="Metadados" bodyClassName="p-0" testId="product-metadata">
                <div>{renderRows(metaRows)}</div>
              </SectionCard>
            </aside>
          </div>
        </div>
      </div>

      {/* Modal de imagem em tela cheia */}
      {isImageFullscreen && hasPhotos && createPortal((
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          onClick={() => setIsImageFullscreen(false)}
          data-testid="product-image-fullscreen"
        >
          <button
            onClick={() => setIsImageFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="product-image-fullscreen-close"
          >
            <Icon name="x" className="w-6 h-6" />
          </button>
          <img
            src={photos[currentImageIndex]}
            alt={`${product.name} - Foto ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                className="absolute left-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="product-image-fullscreen-prev"
              >
                <Icon name="chevron-left" className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                className="absolute right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="product-image-fullscreen-next"
              >
                <Icon name="chevron-right" className="w-8 h-8" />
              </button>
            </>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white tabular-nums">
            {currentImageIndex + 1} / {photos.length}
          </div>
        </div>
      ), window.document.body)}
    </div>
  );
};

/**
 * Opportunity Card Component
 */
const OpportunityCard: React.FC<{ opportunity: BusinessOpportunity }> = ({ opportunity }) => {
  const statusColor = getOpportunityStatusColor(opportunity.status);

  return (
    <div
      className="p-4 rounded-lg"
      style={{
        backgroundColor: 'var(--theme-page-background)',
        border: '1px solid var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className="px-2 py-1 rounded text-xs font-medium"
          style={{
            backgroundColor:
              opportunity.type === 'sale' ? 'var(--theme-success-background)' : 'var(--theme-primary-background)',
            color: opportunity.type === 'sale' ? 'var(--theme-success-color)' : 'var(--theme-primary)',
          }}
        >
          {getOpportunityTypeLabel(opportunity.type)}
        </span>
        <span
          className="px-2 py-1 rounded text-xs font-medium"
          style={{
            backgroundColor: statusColor.bg,
            color: statusColor.text,
          }}
        >
          {opportunity.status === 'active'
            ? 'Ativo'
            : opportunity.status === 'inactive'
            ? 'Inativo'
            : opportunity.status === 'reserved'
            ? 'Reservado'
            : opportunity.status === 'in_negotiation'
            ? 'Em Negociação'
            : opportunity.status === 'completed'
            ? 'Concluído'
            : 'Cancelado'}
        </span>
      </div>

      <div className="mb-3">
        <div className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
          {formatFullPrice(opportunity.pricing.amount)}
        </div>
        {opportunity.pricing.period && (
          <div className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            por{' '}
            {opportunity.pricing.period === 'day'
              ? 'dia'
              : opportunity.pricing.period === 'month'
              ? 'mês'
              : 'ano'}
          </div>
        )}
      </div>

      {opportunity.conditions && (
        <div className="space-y-1 text-sm">
          {opportunity.conditions.negotiable && (
            <div className="flex items-center gap-2">
              <Icon name="check" className="w-4 h-4" style={{ color: 'var(--theme-success-color)' }} />
              <span style={{ color: 'var(--theme-card-text)' }}>Aceita negociação</span>
            </div>
          )}
          {opportunity.conditions.acceptsExchange && (
            <div className="flex items-center gap-2">
              <Icon name="check" className="w-4 h-4" style={{ color: 'var(--theme-success-color)' }} />
              <span style={{ color: 'var(--theme-card-text)' }}>Aceita permuta</span>
            </div>
          )}
          {opportunity.conditions.installments?.available && (
            <div className="flex items-center gap-2">
              <Icon name="check" className="w-4 h-4" style={{ color: 'var(--theme-success-color)' }} />
              <span style={{ color: 'var(--theme-card-text)' }}>
                Parcelamento em até {opportunity.conditions.installments.maxInstallments}x
              </span>
            </div>
          )}
        </div>
      )}

      {opportunity.additionalCosts && opportunity.additionalCosts.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--theme-muted-text)' }}>
            Custos adicionais:
          </div>
          {opportunity.additionalCosts.map((cost, index) => (
            <div key={index} className="flex justify-between text-sm">
              <span style={{ color: 'var(--theme-card-text)' }}>{cost.name}</span>
              <span style={{ color: 'var(--theme-muted-text)' }}>
                {formatPrice(cost.amount)}
                {cost.frequency === 'monthly' ? '/mês' : cost.frequency === 'yearly' ? '/ano' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
